/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState, useMemo } from 'react'
import { Button, OverlayTrigger, Popover } from 'react-bootstrap'
import { Formik, Field, type FormikHelpers } from 'formik'
import DatePicker from '~/components/DatePicker/CustomDatePicker'
import { Select } from '../Select/Select'
import { DocTypeDropdown } from '../pickers/DocTypeDropdown'
import Modal from '../Modal'
import { useServiceContext } from '../../root'
import { useTranslation } from 'react-i18next'
import { useEsignature } from '../../hooks/useEsignature'
import { exportZipCustomReports, getMimeTypeCategories, getFileDetail } from '../../services/api/files'
import { convertDateForServer } from '../../utils/dateFormatting'
import { toast } from '../../services/toast'
import { FsiLifecycleState, SystemFolderType } from '../../constants/fsi'
import Constants from '~/constants/constants'
import { getDatePickerFormat } from '~/utils/dateFormatting'

interface DocumentExportModalProps {
  show: boolean
  handleClose: () => void
  parentId: number
}

interface FormValues {
  sizeUnit: 'KB' | 'MB' | 'GB'
  nameMatchType: 'EQUALS' | 'CONTAINS'
  sizeType: 'LESSER' | 'GREATER'
  createdSpec: 'ON' | 'BEFORE' | 'AFTER'
  modifiedSpec: 'ON' | 'BEFORE' | 'AFTER'
  mimetype: string[] | null
  fsiTypeTemplate: number[] | null
  size: number | null
  exportType: 'SOURCE' | 'PDF'
  createdAt: Date | null
  modifiedAt: Date | null
  name: string | null
  includeEmptyFolders: boolean
  includeSubFolders: boolean
  includeMajorHistoricalVersions: boolean
  generatePropertyFile: boolean
  state: string
  parentId: number
}

const NAME_MATCH_TYPES = [
  { value: 'EQUALS', label: 'OPTION_EQUALS' },
  { value: 'CONTAINS', label: 'OPTION_CONTAINS' }
]

const SIZE_OPTIONS = [
  { value: 'LESSER', label: 'OPTION_IS_LESS_THAN' },
  { value: 'GREATER', label: 'OPTION_IS_GREATER_THAN' }
]

const SIZE_UNIT_OPTIONS = [
  { value: 'KB', label: 'KB' },
  { value: 'MB', label: 'MB' },
  { value: 'GB', label: 'GB' }
]

const CREATED_MODIFIED_SPEC = [
  { value: 'ON', label: 'OPTION_ON' },
  { value: 'BEFORE', label: 'OPTION_BEFORE' },
  { value: 'AFTER', label: 'OPTION_AFTER' }
]

export function DocumentExportModal({ show, handleClose, parentId }: DocumentExportModalProps) {
  const context = useServiceContext()
  const { t } = useTranslation()
  const { signAction } = useEsignature()

  const [folder, setFolder] = useState<{ name?: string; uuid?: string; systemFolderType?: string }>({})
  const [showSubFolderOptions, setShowSubFolderOptions] = useState(true)
  const [possibleStates, setPossibleStates] = useState<Array<{ value: string; label: string }>>([])
  const [possibleMimeTypes, setPossibleMimeTypes] = useState<string[]>([])

  const EXPORT_FORMAT = useMemo(
    () => [
      { value: 'SOURCE', label: t('SOURCE_FORMAT') },
      { value: 'PDF', label: t('BROWSER_DOCS_EXPORT_PDF_WITHOUT_WATERMARKS') }
    ],
    [t]
  )

  // Helper function to get option value for Select components
  const getSelectValue = (options: Array<{ value: any; label: string }>, value: any) => {
    if (!value || !options || options.length === 0) return null
    return options.find((opt) => opt.value === value) || null
  }

  // Memoize initialValues to prevent recreation on every render (depends on parentId prop)
  const initialValues: FormValues = useMemo(
    () => ({
      sizeUnit: 'KB' as const,
      nameMatchType: 'CONTAINS' as const,
      sizeType: 'LESSER' as const,
      createdSpec: 'ON' as const,
      modifiedSpec: 'ON' as const,
      mimetype: null,
      fsiTypeTemplate: null,
      size: null,
      exportType: 'SOURCE' as const,
      createdAt: null,
      modifiedAt: null,
      name: null,
      includeEmptyFolders: false,
      includeSubFolders: false,
      includeMajorHistoricalVersions: false,
      generatePropertyFile: true,
      state: 'ALL_STATES',
      parentId: parentId
    }),
    [parentId]
  )

  useEffect(() => {
    const loadData = async () => {
      try {
        // Load folder details
        const folderData = await getFileDetail(parentId)
        setFolder({
          name: folderData.name,
          uuid: folderData.uuid,
          systemFolderType: folderData.systemFolderType
        })

        // Auto-check subfolders for FOLDER_STRUCTURE
        const isFolderStructure = folderData.uuid === Constants.file.uuid.FOLDER_STRUCTURE

        // Hide subfolder options for TEMPLATES folder
        const isTemplates = folderData.systemFolderType === SystemFolderType.TEMPLATES
        setShowSubFolderOptions(!isTemplates)

        // Load lifecycle states
        const states = [
          { value: 'ALL_STATES', label: t('ALL_STATES') },
          { value: FsiLifecycleState.EFFECTIVE, label: t('EFFECTIVE') },
          { value: FsiLifecycleState.OBSOLETE, label: t('OBSOLETE') },
          { value: FsiLifecycleState.REJECTED, label: t('REJECTED') }
        ]
        setPossibleStates(states)

        // Load mimetype categories
        const mimeTypes = await getMimeTypeCategories()
        setPossibleMimeTypes(mimeTypes)

        // Auto-enable options for FOLDER_STRUCTURE
        if (isFolderStructure) {
          // This will be handled in the form's initial state
        }
      } catch (error) {
        console.error('Error loading export modal data:', error)
        toast.error(t('ERROR_LOADING_DATA'))
      }
    }

    if (show && parentId) {
      loadData()
    }
  }, [show, parentId, t])

  const getSizesInBytes = (fileSize: number | null, actualUnits: string): number | null => {
    if (fileSize === null) return null

    let newSize = 0
    switch (actualUnits) {
      case 'KB':
        newSize = fileSize * 1024
        break
      case 'MB':
        newSize = fileSize * 1024 * 1024
        break
      case 'GB':
        newSize = fileSize * 1024 * 1024 * 1024
        break
      default:
        newSize = fileSize
    }
    return newSize
  }

  const checkLengthAndSpecialChar = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const charLimit = 10
    const exceptThisSymbols = ['e', 'E', '+', '-', '.']

    if (e.currentTarget.value.length + 1 > charLimit || exceptThisSymbols.includes(e.key)) {
      e.preventDefault()
    }
  }

  const handleSubmit = async (values: FormValues, { setSubmitting }: FormikHelpers<FormValues>) => {
    try {
      // Build filter request data
      const filterRequestData: any = {
        parentId: Number(values.parentId), // Ensure number type
        exportType: values.exportType,
        includeSubFolders: values.includeSubFolders,
        includeEmptyFolders: values.includeSubFolders ? values.includeEmptyFolders : false,
        includeMajorHistoricalVersions: values.includeMajorHistoricalVersions,
        generatePropertyFile: values.exportType === 'SOURCE' ? values.generatePropertyFile : false
      }

      // Add optional filters only if they have values
      if (values.size !== null) {
        filterRequestData.size = Number(getSizesInBytes(values.size, values.sizeUnit)) // Ensure number type
        filterRequestData.sizeType = values.sizeType
      }

      if (values.createdAt) {
        filterRequestData.createdAt = Number(convertDateForServer(values.createdAt)) // Ensure number type
        filterRequestData.createdSpec = values.createdSpec
      }

      if (values.modifiedAt) {
        filterRequestData.modifiedAt = Number(convertDateForServer(values.modifiedAt)) // Ensure number type
        filterRequestData.modifiedSpec = values.modifiedSpec
      }

      if (values.name) {
        filterRequestData.name = values.name
        filterRequestData.nameMatchType = values.nameMatchType
      }

      if (values.mimetype && values.mimetype.length > 0) {
        filterRequestData.mimetype = values.mimetype
      }

      if (values.state && values.state !== 'ALL_STATES') {
        filterRequestData.lifecycleState = values.state
      }

      if (values.fsiTypeTemplate && values.fsiTypeTemplate.length > 0) {
        // Ensure all IDs are numbers, not strings
        filterRequestData.fsiTypeTemplate = values.fsiTypeTemplate.map((id) => Number(id))
      }

      // Get e-signature
      const validation = await signAction('ADMIN_ACTION', t('ADMIN_ACTION_DOCUMENT_EXPORT'))

      if (!validation) {
        // E-signature cancelled or failed
        setSubmitting(false)
        return
      }

      // Call export API
      await exportZipCustomReports(filterRequestData, validation as Record<string, unknown>)

      // Show success message
      toast.success(t('EXPORT_IN_PROCCESS_TITLE'))

      // Close modal
      handleClose()
    } catch (error: any) {
      console.error('Error exporting documents:', error)
      toast.error(error?.message || t('ERROR_EXPORTING_DOCUMENTS'))
    } finally {
      setSubmitting(false)
    }
  }

  const doClose = () => {
    handleClose()
  }

  // Check if folder is FOLDER_STRUCTURE (auto-enable subfolders) - memoize to prevent recalculation
  const isFolderStructure = useMemo(() => {
    return folder.uuid === Constants.file.uuid.FOLDER_STRUCTURE
  }, [folder.uuid])

  // Memoize initial values to prevent Formik re-initialization on every render
  const formInitialValues = useMemo(
    () => ({
      ...initialValues,
      includeSubFolders: isFolderStructure,
      includeEmptyFolders: isFolderStructure
    }),
    [isFolderStructure]
  )

  const popoverFilenameInfo = (
    <Popover
      id="filename-info-popover"
      style={{
        zIndex: 9999,
        position: 'absolute',
        display: 'block',
        opacity: 1,
        visibility: 'visible'
      }}
    >
      <Popover.Body>
        <div dangerouslySetInnerHTML={{ __html: t('DOCS_EXPORT_FILENAME_ONHOVER_INFO_TXT') }}></div>
      </Popover.Body>
    </Popover>
  )

  return (
    <Modal title={t('BROWSER_DOCS_EXPORT_H_TITLE')} show={show} handleClose={doClose} size="md">
      <Formik initialValues={formInitialValues} enableReinitialize onSubmit={handleSubmit}>
        {({ values, setFieldValue, handleSubmit, isSubmitting }) => (
          <>
            <Modal.Body>
              <div>
                {/* Folder Name */}
                <div className="form-group wparag">
                  <label className="mb0">{t('FOLDER')}</label>
                  <p className="fs12">{folder.name || ''}</p>
                </div>

                {/* Filename Filter */}
                <div className="inpt-wrpr date-filter">
                  <div className="form-group w100p">
                    <label htmlFor="in-name" className="mb0">
                      {t('FILENAME')}{' '}
                      <OverlayTrigger
                        trigger={['hover', 'focus']}
                        placement="top"
                        overlay={popoverFilenameInfo}
                        delay={{ show: 250, hide: 400 }}
                      >
                        <span style={{ display: 'inline-block' }}>
                          <button
                            className="onhover_info"
                            type="button"
                            tabIndex={0}
                            aria-label="Info"
                            style={{
                              display: 'inline-block',
                              width: '14px',
                              height: '14px',
                              border: 'none',
                              outline: 'none',
                              background: 'url(/assets/images/icons/tooltip_i_icon.svg) no-repeat center center',
                              backgroundSize: 'contain',
                              verticalAlign: 'middle',
                              marginLeft: '4px',
                              cursor: 'pointer'
                            }}
                          ></button>
                        </span>
                      </OverlayTrigger>
                    </label>
                    <Select
                      id="in-nameMatchType"
                      name="nameMatchType"
                      value={values.nameMatchType}
                      isClearable={false}
                      options={NAME_MATCH_TYPES.map((opt) => ({ value: opt.value, label: t(opt.label) }))}
                      onChange={(option: any) => {
                        setFieldValue('nameMatchType', option?.value || 'CONTAINS')
                      }}
                    />
                    <Field
                      type="text"
                      name="name"
                      style={{ float: 'left', marginTop: '8px' }}
                      maxLength={255}
                      className="form-control mb15"
                    />
                  </div>
                </div>

                {/* File Format Filter */}
                <div className="inpt-wrpr user-filter">
                  <div className="form-group w100p">
                    <label className="mb0">{t('FILE_FORMAT')}</label>
                    <Select
                      id="in-possibleMimeTypes"
                      name="mimetype"
                      placeholder={t('SELECT_FILE_FORMATS')}
                      value={values.mimetype}
                      isClearable={true}
                      isMulti={true}
                      options={possibleMimeTypes.map((item) => ({
                        value: item,
                        label: t(item)
                      }))}
                      onChange={(option: any) => {
                        setFieldValue('mimetype', option && option.length > 0 ? option.map((o: any) => o.value) : null)
                      }}
                    />
                  </div>
                </div>

                {/* State Filter */}
                <div className="inpt-wrpr date-filter">
                  <div className="form-group w100p">
                    <label className="mb0">{t('STATE')}</label>
                    <Select
                      id="in-state"
                      options={possibleStates}
                      name="state"
                      placeholder={t('SELECT_STATE')}
                      value={values.state}
                      isClearable={false}
                      onChange={(option: any) => {
                        setFieldValue('state', option?.value || 'ALL_STATES')
                      }}
                    />
                  </div>
                </div>

                {/* Document Type Filter */}
                <div className="inpt-wrpr date-filter">
                  <div className="form-group w100p">
                    <label className="mb0">{t('DOC_TYPE')}</label>
                    <DocTypeDropdown
                      name="doctypes"
                      isSingle={false}
                      value={
                        values.fsiTypeTemplate
                          ? values.fsiTypeTemplate.map((id) => ({
                              value: id,
                              label: `Type ${id}`
                            }))
                          : null
                      }
                      onChange={(option: any) => {
                        setFieldValue('fsiTypeTemplate', option ? option.map((o: any) => o.value) : null)
                      }}
                    />
                  </div>
                </div>

                {/* File Size Filter */}
                <div className="inpt-wrpr">
                  <div className="form-group w100p">
                    <label htmlFor="in-size" className="mb0" style={{ display: 'block' }}>
                      {t('SIZE')}
                    </label>
                    <Select
                      id="in-size"
                      name="sizeType"
                      value={values.sizeType}
                      options={SIZE_OPTIONS.map((opt) => ({ value: opt.value, label: t(opt.label) }))}
                      isClearable={false}
                      onChange={(option: any) => {
                        setFieldValue('sizeType', option?.value || 'LESSER')
                      }}
                    />

                    <Field
                      type="number"
                      style={{ width: '60%', float: 'left', marginTop: '8px' }}
                      name="size"
                      className="form-control"
                      onKeyPress={checkLengthAndSpecialChar}
                    />

                    <div style={{ float: 'left', width: '35%', position: 'relative', left: '5%', marginTop: '8px' }}>
                      <Select
                        id="in-sizeu"
                        name="sizeUnit"
                        isClearable={false}
                        value={values.sizeUnit}
                        options={SIZE_UNIT_OPTIONS}
                        onChange={(option: any) => {
                          setFieldValue('sizeUnit', option?.value || 'KB')
                        }}
                      />
                    </div>
                    <div style={{ clear: 'both' }}></div>
                  </div>
                </div>

                {/* Created Date Filter */}
                <div className="inpt-wrpr">
                  <div className="form-group w100p">
                    <label htmlFor="created-date" className="mb0">
                      {t('REPORTS_FILTER_LABEL_CREATED')}
                    </label>
                    <Select
                      id="created-spec"
                      options={CREATED_MODIFIED_SPEC.map((opt) => ({ value: opt.value, label: t(opt.label) }))}
                      name="createdSpec"
                      value={values.createdSpec}
                      isClearable={false}
                      onChange={(option: any) => {
                        setFieldValue('createdSpec', option?.value || 'ON')
                      }}
                    />
                    <div className="scnd-inp-vs" style={{ marginTop: '8px' }}>
                      <DatePicker
                        selected={values.createdAt}
                        onChange={(date: Date | null) => {
                          setFieldValue('createdAt', date)
                        }}
                        showYearDropdown
                        className="form-control field-control w100p"
                        id="created-date"
                      />
                    </div>
                  </div>
                </div>

                {/* Modified Date Filter */}
                <div className="inpt-wrpr">
                  <div className="form-group w100p">
                    <label htmlFor="modified-date" className="mb0">
                      {t('LAST_MODIFIED')}
                    </label>
                    <Select
                      id="modified-spec"
                      options={CREATED_MODIFIED_SPEC.map((opt) => ({ value: opt.value, label: t(opt.label) }))}
                      name="modifiedSpec"
                      value={values.modifiedSpec}
                      isClearable={false}
                      onChange={(option: any) => {
                        setFieldValue('modifiedSpec', option?.value || 'ON')
                      }}
                    />
                    <div className="scnd-inp-vs" style={{ marginTop: '8px' }}>
                      <DatePicker
                        selected={values.modifiedAt}
                        onChange={(date: Date | null) => {
                          setFieldValue('modifiedAt', date)
                        }}
                        showYearDropdown
                        className="form-control field-control w100p"
                        id="modified-date"
                      />
                    </div>
                  </div>
                </div>

                {/* Export Format */}
                <div className="inpt-wrpr user-filter">
                  <div className="form-group w100p">
                    <label className="mb0">{t('EXPORT_FORMAT')}</label>
                    <Select
                      id="in-exportType"
                      options={EXPORT_FORMAT}
                      name="exportType"
                      value={values.exportType}
                      isClearable={false}
                      onChange={(option: any) => {
                        setFieldValue('exportType', option?.value || 'SOURCE')
                      }}
                    />
                  </div>
                </div>

                {/* Include Subfolders - Hide for TEMPLATES folder */}
                {showSubFolderOptions && (
                  <div className="form-group checkbox-form-group" style={{ marginBottom: '10px' }}>
                    <input
                      type="checkbox"
                      id="includeSubFolders"
                      name="includeSubFolders"
                      checked={values.includeSubFolders}
                      onChange={(event) => {
                        const checked = event.target.checked
                        setFieldValue('includeSubFolders', checked)
                        // Uncheck empty folders if subfolders unchecked
                        if (!checked) {
                          setFieldValue('includeEmptyFolders', false)
                        }
                      }}
                      disabled={isFolderStructure}
                    />
                    <label htmlFor="includeSubFolders" className="fw-bold" style={{ fontSize: '12px' }}>
                      <div className="chkbc"></div>
                      {t('BROWSER_DOCS_EXPORT_INCL_SUBFOLDERS')}
                    </label>
                  </div>
                )}

                {/* Include Empty Folders - Only show if subfolders enabled */}
                {showSubFolderOptions && values.includeSubFolders && (
                  <div className="form-group checkbox-form-group" style={{ marginBottom: '10px' }}>
                    <input
                      type="checkbox"
                      id="includeEmptyFolders"
                      name="includeEmptyFolders"
                      checked={values.includeEmptyFolders}
                      onChange={(event) => {
                        setFieldValue('includeEmptyFolders', event.target.checked)
                      }}
                      disabled={isFolderStructure}
                    />
                    <label htmlFor="includeEmptyFolders" className="fw-bold" style={{ fontSize: '12px' }}>
                      <div className="chkbc"></div>
                      {t('BROWSER_DOCS_EXPORT_INCL_EMPTY_FOLDERS')}
                    </label>
                  </div>
                )}

                {/* Generate Property File - Only for SOURCE format */}
                {values.exportType === 'SOURCE' && (
                  <div className="form-group checkbox-form-group" style={{ marginBottom: '10px' }}>
                    <input
                      type="checkbox"
                      id="generatePropertyFile"
                      name="generatePropertyFile"
                      checked={values.generatePropertyFile}
                      onChange={(event) => {
                        setFieldValue('generatePropertyFile', event.target.checked)
                      }}
                    />
                    <label htmlFor="generatePropertyFile" className="fw-bold" style={{ fontSize: '12px' }}>
                      <div className="chkbc"></div>
                      {t('BROWSER_DOCS_EXPORT_GENERATE_PROP_FILES')}
                    </label>
                  </div>
                )}

                {/* Include Major Historical Versions */}
                <div className="form-group checkbox-form-group" style={{ marginBottom: '10px' }}>
                  <input
                    type="checkbox"
                    id="includeMajorHistoricalVersions"
                    name="includeMajorHistoricalVersions"
                    checked={values.includeMajorHistoricalVersions}
                    onChange={(event) => {
                      setFieldValue('includeMajorHistoricalVersions', event.target.checked)
                    }}
                  />
                  <label htmlFor="includeMajorHistoricalVersions" className="fw-bold" style={{ fontSize: '12px' }}>
                    <div className="chkbc"></div>
                    {t('BROWSER_DOCS_EXPORT_INCLUDE_HISTORICAL_VERSIONS')}
                  </label>
                </div>
              </div>
            </Modal.Body>

            <Modal.Footer>
              <Button variant="link" onClick={doClose} disabled={isSubmitting}>
                {t('CANCEL')}
              </Button>
              <Button variant="primary" onClick={() => handleSubmit()} disabled={isSubmitting}>
                {isSubmitting ? t('EXPORTING...') : t('OK')}
              </Button>
            </Modal.Footer>
          </>
        )}
      </Formik>
    </Modal>
  )
}

export default DocumentExportModal
